/**
 * Shop By Vehicle Hero — step-by-step YMM wizard
 *
 * Architecture:
 * - The wizard UI is standalone (own markup, CSS, state).
 * - ConverMax is the source of truth for fitment data. We use:
 *     (a) The header widget's <select> elements at #cm-ymm as our data source
 *         (ConverMax populates Year; dispatching change triggers Make; etc.).
 *     (b) window.Convermax.setVehicle() / addVehicle() to persist the final
 *         selection to ConverMax's garage so the header reflects it on the
 *         next page and PDP fitment works site-wide.
 * - On Shop, we redirect to /collections/all?year=X&make=Y&model=Z —
 *   ConverMax on the collection page handles product filtering + fitment UI.
 *
 * Why drive the header widget instead of a pure REST call:
 *   The public ConverMax /search.json endpoint does not return Make/Model
 *   facet values for OE Wheels' catalog without ConverMax's internal
 *   mode/query config. The header widget already handles this correctly, so
 *   we use it as a data proxy. This incidentally syncs the header UI, which
 *   is the desired behavior anyway (user's vehicle follows them).
 */
(function () {
  'use strict';

  const wizard = document.querySelector('[data-sbv-wizard]');
  if (!wizard) return;

  // ---- DOM refs ----
  const els = {
    Year: wizard.querySelector('[data-sbv-field="Year"]'),
    Make: wizard.querySelector('[data-sbv-field="Make"]'),
    Model: wizard.querySelector('[data-sbv-field="Model"]'),
    shop: wizard.querySelector('[data-sbv-shop]'),
    clear: wizard.querySelector('[data-sbv-clear]'),
    prompt: wizard.querySelector('[data-sbv-prompt]'),
    status: wizard.querySelector('[data-sbv-status]'),
    steps: wizard.querySelectorAll('.sbv-step'),
  };
  const urlTemplate = wizard.dataset.shopUrlTemplate ||
    '/collections/all?year=__YEAR__&make=__MAKE__&model=__MODEL__';

  // ---- State ----
  const state = { Year: '', Make: '', Model: '' };
  const FIELDS = ['Year', 'Make', 'Model'];

  // ---- ConverMax widget select lookup ----
  // The header widget renders selects with various class/id patterns. We find
  // them by their label attribute or position within #cm-ymm.
  function findCMSelect(field) {
    const container = document.querySelector('#cm-ymm');
    if (!container) return null;

    // Try aria-label or name matching
    const labelMatch = container.querySelector(
      `select[aria-label="${field}" i], select[name="${field}" i], select[name="${field.toLowerCase()}" i]`
    );
    if (labelMatch) return labelMatch;

    // Fallback: selects appear in Year, Make, Model order within .cm_select
    const selects = container.querySelectorAll('.cm_select_inner-select, select');
    const idx = FIELDS.indexOf(field);
    return selects[idx] || null;
  }

  // An option is "real data" if its value is a non-empty, non-loading string.
  // ConverMax uses "...loading..." as a placeholder value while fetching.
  function hasRealOptions(selectEl) {
    if (!selectEl) return false;
    return Array.from(selectEl.options).some(o => {
      const v = (o.value || '').trim();
      return v && !/^\.+loading\.+$/i.test(v) && v !== 'loading';
    });
  }

  function waitForConverMaxReady(timeoutMs = 15000) {
    return new Promise((resolve, reject) => {
      const start = Date.now();
      const check = () => {
        const yearSel = findCMSelect('Year');
        if (hasRealOptions(yearSel)) {
          resolve();
          return;
        }
        if (Date.now() - start > timeoutMs) {
          reject(new Error('ConverMax widget did not load in time.'));
          return;
        }
        setTimeout(check, 150);
      };
      check();
    });
  }

  function waitForCMSelectPopulated(field, prevSignature, timeoutMs = 10000) {
    return new Promise((resolve, reject) => {
      const start = Date.now();
      const check = () => {
        const sel = findCMSelect(field);
        if (sel && hasRealOptions(sel) && optionsSignature(sel) !== prevSignature) {
          resolve(sel);
          return;
        }
        if (Date.now() - start > timeoutMs) {
          reject(new Error(`ConverMax ${field} select did not populate.`));
          return;
        }
        setTimeout(check, 150);
      };
      check();
    });
  }

  function optionsSignature(sel) {
    return sel ? Array.from(sel.options).map(o => o.value).join('|') : '';
  }

  // ---- Populate our wizard select from a CM select ----
  function mirrorOptions(sourceSelect, targetSelect, placeholder) {
    const options = Array.from(sourceSelect.options)
      .filter(o => {
        const v = (o.value || '').trim();
        return v && !/^\.+loading\.+$/i.test(v) && v !== 'loading';
      })
      .map(o => ({ value: o.value, text: o.text }));

    targetSelect.innerHTML =
      `<option value="">${placeholder}</option>` +
      options.map(o =>
        `<option value="${escapeHtml(o.value)}">${escapeHtml(o.text)}</option>`
      ).join('');
    targetSelect.disabled = false;
    targetSelect.classList.remove('is-hidden');
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  }

  // ---- Drive the CM widget for data fetching ----
  function setCMFieldAndFetchNext(field, value, nextField) {
    const cmSel = findCMSelect(field);
    if (!cmSel) return Promise.reject(new Error(`ConverMax ${field} select not found.`));

    const nextSel = findCMSelect(nextField);
    const prevSignature = optionsSignature(nextSel);

    cmSel.value = value;
    cmSel.dispatchEvent(new Event('change', { bubbles: true }));

    return waitForCMSelectPopulated(nextField, prevSignature);
  }

  // ---- Wizard step UI ----
  function advanceStep(stepNum) {
    els.steps.forEach(s => {
      const n = parseInt(s.dataset.step, 10);
      s.classList.toggle('is-complete', n < stepNum);
      s.classList.toggle('is-active', n === stepNum);
    });

    const prompts = {
      2: 'What is the <strong>Make</strong> of your vehicle?',
      3: 'What is the <strong>Model</strong> of your vehicle?',
      4: "You're ready \u2014 click <strong>Shop</strong> to see results.",
    };
    if (prompts[stepNum]) {
      els.prompt.innerHTML = prompts[stepNum];
    }
  }

  function setStatus(msg) {
    if (els.status) els.status.textContent = msg || '';
  }

  function resetDownstream(fromField) {
    const idx = FIELDS.indexOf(fromField);
    for (let i = idx + 1; i < FIELDS.length; i++) {
      const f = FIELDS[i];
      state[f] = '';
      if (els[f]) {
        els[f].innerHTML = `<option value="">${f}</option>`;
        els[f].disabled = true;
        els[f].classList.add('is-hidden');
      }
    }
    els.shop.disabled = true;
  }

  // ---- Init ----
  async function init() {
    setStatus('Loading vehicles\u2026');
    try {
      await waitForConverMaxReady();
    } catch (err) {
      console.warn('[sbv] ConverMax not ready:', err.message);
      setStatus('Vehicle data is taking a while to load. Please refresh if this persists.');
      return;
    }

    const cmYear = findCMSelect('Year');
    if (!cmYear) {
      setStatus('Vehicle selector unavailable.');
      return;
    }

    mirrorOptions(cmYear, els.Year, 'Year');
    els.Year.disabled = false;
    setStatus('');
  }

  // ---- Event handlers ----
  els.Year.addEventListener('change', async (e) => {
    const value = e.target.value;
    state.Year = value;
    resetDownstream('Year');
    if (!value) return;

    advanceStep(2);
    setStatus('Loading makes\u2026');
    try {
      const cmMake = await setCMFieldAndFetchNext('Year', value, 'Make');
      mirrorOptions(cmMake, els.Make, 'Make');
      setStatus('');
    } catch (err) {
      console.warn('[sbv] Make fetch failed:', err.message);
      setStatus('Could not load makes. Please try again.');
    }
  });

  els.Make.addEventListener('change', async (e) => {
    const value = e.target.value;
    state.Make = value;
    resetDownstream('Make');
    if (!value) return;

    advanceStep(3);
    setStatus('Loading models\u2026');
    try {
      const cmModel = await setCMFieldAndFetchNext('Make', value, 'Model');
      mirrorOptions(cmModel, els.Model, 'Model');
      setStatus('');
    } catch (err) {
      console.warn('[sbv] Model fetch failed:', err.message);
      setStatus('Could not load models. Please try again.');
    }
  });

  els.Model.addEventListener('change', (e) => {
    state.Model = e.target.value;
    if (!state.Model) {
      els.shop.disabled = true;
      return;
    }
    advanceStep(4);
    els.shop.disabled = false;
  });

  els.shop.addEventListener('click', () => {
    if (!state.Year || !state.Make || !state.Model) return;

    let searchParams = null;
    try {
      if (window.Convermax && typeof window.Convermax.setVehicle === 'function') {
        // ConverMax baseFitmentFields require Submodel. Wheels usually have no Submodel, pass empty.
        window.Convermax.setVehicle({
          Year: state.Year,
          Make: state.Make,
          Model: state.Model,
          Submodel: '',
        });
      }
      if (window.Convermax && typeof window.Convermax.getVehicleSearchParams === 'function') {
        searchParams = window.Convermax.getVehicleSearchParams();
      }
    } catch (err) {
      console.warn('[sbv] setVehicle failed:', err.message);
    }

    // ConverMax uses path-style URL params: /collections/all?Year/2020/Make/Chevrolet/Model/Silverado-1500
    // getVehicleSearchParams() returns exactly that format. Fall back to query template if unavailable.
    let url;
    if (searchParams) {
      url = '/collections/all?' + searchParams;
    } else {
      url = urlTemplate
        .replace('__YEAR__', encodeURIComponent(state.Year))
        .replace('__MAKE__', encodeURIComponent(state.Make))
        .replace('__MODEL__', encodeURIComponent(state.Model));
    }

    // Small delay so setVehicle's localStorage write completes before navigation
    setTimeout(() => { window.location.href = url; }, 50);
  });

  els.clear.addEventListener('click', (e) => {
    e.preventDefault();
    state.Year = state.Make = state.Model = '';
    els.Year.value = '';
    resetDownstream('Year');
    // Reset Year itself too
    els.Year.selectedIndex = 0;
    // Reset steps to step 1
    els.steps.forEach(s => {
      const n = parseInt(s.dataset.step, 10);
      s.classList.toggle('is-active', n === 1);
      s.classList.remove('is-complete');
    });
    els.prompt.innerHTML = 'What is the <strong>Year</strong> of your vehicle?';
    setStatus('');

    // Also clear ConverMax's current vehicle so header resets
    try {
      if (window.Convermax && typeof window.Convermax.discardVehicle === 'function') {
        window.Convermax.discardVehicle();
      }
    } catch (_) { /* ignore */ }
  });

  // Start
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
